import React, { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Info } from "lucide-react";
import { apiClient } from "../../api/client";
import { AlertBanner } from "../../components/common";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { ConnectionTable, type Connection } from "./ConnectionTable";
import { GeminiConnectCard } from "./GeminiConnectCard";
import { usePluginTranslation } from "./translations";

export interface IntegrationPanelProps {
  publicationUrl?: string | null;
  marketplaceUrl?: string | null;
}

export const IntegrationPanel: React.FC<IntegrationPanelProps> = ({
  publicationUrl,
  marketplaceUrl,
}) => {
  const { user } = useAuth();
  const { language, direction } = useLanguage();
  const t = usePluginTranslation();

  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 10;

  const currentUserIdRef = useRef(user?.id);
  const abortRef = useRef<AbortController | null>(null);
  const revokeAbortRef = useRef<AbortController | null>(null);

  const verifiedUrl = publicationUrl ?? marketplaceUrl ?? null;

  useEffect(() => {
    return () => {
      revokeAbortRef.current?.abort();
    };
  }, []);

  const fetchConnections = useCallback(async () => {
    if (!user?.id) {
      setConnections([]);
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiErr } = await apiClient.GET(
        "/api/v1/plugin/connections",
        {
          params: { query: { limit, offset } },
          signal: controller.signal,
        },
      );

      if (controller.signal.aborted || currentUserIdRef.current !== user.id) {
        return;
      }

      if (apiErr || !data) {
        setError(t("state.error"));
        setConnections([]);
      } else {
        setConnections(data.items ?? []);
      }
    } catch {
      if (!controller.signal.aborted && currentUserIdRef.current === user.id) {
        setError(t("state.error"));
        setConnections([]);
      }
    } finally {
      if (!controller.signal.aborted && currentUserIdRef.current === user.id) {
        setLoading(false);
      }
    }
  }, [user?.id, offset, limit, t]);

  useEffect(() => {
    currentUserIdRef.current = user?.id;
    abortRef.current?.abort();
    revokeAbortRef.current?.abort();
    void fetchConnections();

    return () => {
      abortRef.current?.abort();
    };
  }, [user?.id, fetchConnections]);

  const handleRevoke = async (id: string) => {
    if (revokingId) return;
    setRevokingId(id);
    setFeedback(null);
    setError(null);
    revokeAbortRef.current?.abort();
    const controller = new AbortController();
    revokeAbortRef.current = controller;

    try {
      const { error: apiErr } = await apiClient.DELETE(
        "/api/v1/plugin/connections/{id}",
        {
          params: { path: { id } },
          signal: controller.signal,
        },
      );

      if (controller.signal.aborted || currentUserIdRef.current !== user?.id) {
        return;
      }

      if (apiErr) {
        setError(t("state.revoke_error"));
      } else {
        setConnections((prev) =>
          prev.map((c) =>
            c.id === id ? { ...c, revoked_at: new Date().toISOString() } : c,
          ),
        );
        setFeedback(t("state.revoke_success"));
      }
    } catch {
      if (!controller.signal.aborted && currentUserIdRef.current === user?.id) {
        setError(t("state.revoke_error"));
      }
    } finally {
      if (!controller.signal.aborted && currentUserIdRef.current === user?.id) {
        setRevokingId(null);
      }
    }
  };

  return (
    <section
      data-testid="integration-panel"
      dir={direction}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-6 space-y-6"
    >
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          {t("panel.title")}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t("panel.description")}
        </p>
      </div>

      <GeminiConnectCard onTokenCreated={() => void fetchConnections()} />

      <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        <div className="flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-200">
          <Info className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span>{t("guidance.title")}</span>
        </div>
        <p>
          <bdi>{t("guidance.beta_vs_published")}</bdi>
        </p>
        <p>
          <bdi>{t("guidance.account_wide_events")}</bdi>
        </p>
        <p>
          <bdi>{t("guidance.events_fallback")}</bdi>
        </p>
        {verifiedUrl && (
          <div className="pt-1">
            <a
              href={verifiedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 underline underline-offset-2"
            >
              <span>
                {language === "ar"
                  ? "عرض الإضافة المعتمدة في المتجر"
                  : "View Verified Marketplace Listing"}
              </span>
              <ExternalLink
                className="w-3.5 h-3.5 shrink-0"
                aria-hidden="true"
              />
              <span className="sr-only">
                (
                {language === "ar"
                  ? "يفتح في نافذة جديدة"
                  : "opens in a new tab"}
                )
              </span>
            </a>
          </div>
        )}
      </div>

      {error && (
        <AlertBanner
          variant="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}
      {feedback && (
        <p
          role="status"
          className="text-xs text-emerald-600 dark:text-emerald-400 font-medium"
        >
          {feedback}
        </p>
      )}

      <ConnectionTable
        connections={connections}
        loading={loading}
        revokingId={revokingId}
        onRevoke={(id) => void handleRevoke(id)}
        language={language}
        t={t}
        offset={offset}
        limit={limit}
        onPrevPage={() => setOffset(Math.max(0, offset - limit))}
        onNextPage={() => setOffset(offset + limit)}
      />
    </section>
  );
};
